/**
 * Quran Analyser - Enhanced Interactive JavaScript
 * 
 * Features:
 * - Smart search with reference parsing
 * - AI-powered search disambiguation
 * - Auto-loading AI analysis
 * - Interactive chat for questions
 * - Commonly misunderstood verses exploration
 */

(function () {
    'use strict';

    // ============================================
    // CONFIGURATION
    // ============================================

    const API_BASE = 'https://api.alquran.cloud/v1';
    const BACKEND_API = 'https://api.freequrans.co.uk/api';

    // Surah data for smart search
    const SURAHS = [
        { num: 1, en: 'Al-Fatiha', ar: 'الفاتحة', alt: ['fatiha', 'opening', 'fatihah'], verses: 7 },
        { num: 2, en: 'Al-Baqarah', ar: 'البقرة', alt: ['baqara', 'baqarah', 'cow'], verses: 286 },
        { num: 3, en: 'Aal-Imran', ar: 'آل عمران', alt: ['imran', 'ali imran'], verses: 200 },
        { num: 4, en: 'An-Nisa', ar: 'النساء', alt: ['nisa', 'nisaa', 'women'], verses: 176 },
        { num: 5, en: 'Al-Maidah', ar: 'المائدة', alt: ['maidah', 'maida', 'table'], verses: 120 },
        { num: 9, en: 'At-Tawbah', ar: 'التوبة', alt: ['tawbah', 'tawba', 'repentance'], verses: 129 },
        { num: 12, en: 'Yusuf', ar: 'يوسف', alt: ['yusuf', 'joseph'], verses: 111 },
        { num: 18, en: 'Al-Kahf', ar: 'الكهف', alt: ['kahf', 'cave'], verses: 110 },
        { num: 19, en: 'Maryam', ar: 'مريم', alt: ['maryam', 'mary'], verses: 98 },
        { num: 36, en: 'Ya-Sin', ar: 'يس', alt: ['yasin', 'yaseen'], verses: 83 },
        { num: 49, en: 'Al-Hujurat', ar: 'الحجرات', alt: ['hujurat', 'rooms'], verses: 18 },
        { num: 55, en: 'Ar-Rahman', ar: 'الرحمن', alt: ['rahman', 'merciful'], verses: 78 },
        { num: 56, en: 'Al-Waqiah', ar: 'الواقعة', alt: ['waqiah', 'waqia', 'event'], verses: 96 },
        { num: 67, en: 'Al-Mulk', ar: 'الملك', alt: ['mulk', 'sovereignty', 'dominion'], verses: 30 },
        { num: 112, en: 'Al-Ikhlas', ar: 'الإخلاص', alt: ['ikhlas', 'sincerity'], verses: 4 },
        { num: 113, en: 'Al-Falaq', ar: 'الفلق', alt: ['falaq', 'daybreak', 'dawn'], verses: 5 },
        { num: 114, en: 'An-Nas', ar: 'الناس', alt: ['nas', 'naas', 'mankind', 'people'], verses: 6 }
    ];

    // Famous verses for quick reference
    const FAMOUS_VERSES = {
        'ayatul kursi': { surah: 2, ayah: 255 },
        'throne verse': { surah: 2, ayah: 255 },
        'kursi': { surah: 2, ayah: 255 },
        'no compulsion': { surah: 2, ayah: 256 },
        'no compulsion in religion': { surah: 2, ayah: 256 },
        'save a life': { surah: 5, ayah: 32 },
        'saves all humanity': { surah: 5, ayah: 32 },
        'lailatul qadr': { surah: 97, ayah: 1 },
        'night of power': { surah: 97, ayah: 1 },
        'bismillah': { surah: 1, ayah: 1 }
    };

    // Verse-specific Q&A (attached to verses, not just pills)
    const VERSE_QA = {
        // === SECTION 1: Quranic Insights ===
        '3:45': {
            question: "What does the Quran say about Jesus?",
            answer: "The Quran honors Jesus (Isa) as the Messiah, born of a virgin, who performed miracles by God's permission. He is called 'a Word from God' and 'a Spirit from Him'—one of the mightiest prophets."
        },
        '19:30': {
            question: "What did baby Jesus say from the cradle?",
            answer: "In the Quran, Jesus spoke as an infant: 'I am a servant of God. He has given me the Scripture and made me a prophet. He made me blessed wherever I may be.' A beautiful miracle of his prophethood."
        },
        '19:16': {
            question: "How does the Quran honor Mary?",
            answer: "Mary (Maryam) has an entire chapter named after her—the only woman so honored. The Quran calls her 'chosen above all women of the world' and details her purity, devotion, and the miracle of Jesus' birth."
        },
        '4:1': {
            question: "What rights does Islam give women?",
            answer: "The Quran established women's rights 1400 years ago: inheritance, property ownership, consent in marriage, and spiritual equality. This verse reminds us all humans—men and women—were created from one soul."
        },
        '21:107': {
            question: "Why was Prophet Muhammad sent?",
            answer: "'We have not sent you except as a mercy to all the worlds.' This verse summarizes the Prophet's mission—not to condemn, but to bring mercy, compassion, and guidance to all of humanity."
        },
        '39:53': {
            question: "Can all sins be forgiven?",
            answer: "'Do not despair of God's mercy. Indeed, God forgives all sins.' No matter what you've done, the door to forgiveness is always open. God's mercy encompasses everything—even the gravest mistakes."
        },
        '51:56': {
            question: "What is the purpose of life?",
            answer: "'I did not create jinn and humans except to worship Me.' Worship here means to know God, love Him, and live in alignment with His guidance—finding peace and purpose through connection with the Divine."
        },
        '2:256': {
            question: "Does Islam force people to convert?",
            answer: "'There is no compulsion in religion.' This verse is crystal clear—faith must come from the heart, freely. No one can be forced to believe, and Islam protects everyone's right to their own beliefs."
        },
        '5:32': {
            question: "How valuable is human life in Islam?",
            answer: "'Whoever saves a life, it is as if he has saved all of humanity.' Every single human life is infinitely precious. This verse shows Islam's profound respect for the sanctity of human life."
        },
        // === SECTION 2: Commonly Misunderstood ===
        '9:5': {
            question: "Why does the Quran say 'kill them wherever you find them'?",
            answer: "This verse addresses specific treaty-breakers. Read <a href='#' class='qa-verse-link' data-verse='9:4'>the previous verse</a> (honoring treaties) and <a href='#' class='qa-verse-link' data-verse='9:6'>the next verse</a> (giving refuge). Classical scholars confirm this was about a specific conflict, not a general rule."
        },
        '4:34': {
            question: "Does the Quran permit domestic violence?",
            answer: "The word 'idribuhunna' has 20+ meanings in Arabic. The Prophet ﷺ never struck any wife and said 'The best of you are those best to their wives' (Tirmidhi). Scholars explain this as symbolic separation, not physical harm."
        },
        '2:191': {
            question: "Is the Quran violent against non-believers?",
            answer: "Read <a href='#' class='qa-verse-link' data-verse='2:190'>the previous verse</a> first: 'Fight those who fight you but do not transgress.' This is self-defense for Muslims expelled from their homes. The Prophet ﷺ forbade killing civilians."
        },
        '4:89': {
            question: "Does Islam prescribe death for apostasy?",
            answer: "This verse refers to wartime defectors who joined enemies—a military treason issue, not religious belief. Read <a href='#' class='qa-verse-link' data-verse='4:90'>the next verse</a>: those who seek peace must be left alone. The Quran says 'no compulsion in religion' (2:256)."
        },
        '8:12': {
            question: "Does the Quran command beheading?",
            answer: "This verse describes the Battle of Badr where 313 Muslims faced 1000 attackers. It's God addressing angels in that specific battle—not a general command. War verses have war contexts."
        },
        '9:29': {
            question: "Why should non-Muslims pay jizya tax?",
            answer: "Jizya was a tax in exchange for military exemption and state protection—lower than zakat for Muslims. Non-Muslims kept their own laws, courts, and religious practices. It was security, not humiliation."
        },
        '4:3': {
            question: "Why does Islam allow polygamy?",
            answer: "This verse was revealed after war left many widows and orphans. It permits—but doesn't command—marrying up to 4 if you can be perfectly just. The same verse warns: 'if you fear injustice, then only one.' Most Muslims are monogamous."
        },
        '18:86': {
            question: "Does the Quran say the sun sets in muddy water?",
            answer: "The Arabic 'wajada' means 'he found/perceived'—describing Dhul-Qarnayn's viewpoint at a western shore. This is observational language, like saying 'beautiful sunset.' Not literal cosmology."
        },
        '65:4': {
            question: "Does the Quran permit child marriage?",
            answer: "This verse discusses waiting periods for divorce—not marriage age. The word 'lam yahidna' means women who haven't menstruated due to illness or menopause, not children. The Quran requires maturity and consent for marriage."
        },
        // === SECTION 3: Faith & Reason ===
        '5:46': {
            question: "Does the Quran say the Gospel (Injeel) is uncorrupted?",
            answer: "The Quran confirms the original Injeel revealed to Jesus—but also states that scriptures were 'altered' and 'distorted' (<a href='#' class='qa-verse-link' data-verse='2:75'>2:75</a>, <a href='#' class='qa-verse-link' data-verse='4:46'>4:46</a>, <a href='#' class='qa-verse-link' data-verse='5:13'>5:13</a>). Muslims believe the original message was true; later human editing introduced changes."
        },
        '5:13': {
            question: "What does 'corruption of scripture' mean?",
            answer: "The Quran uses 'tahrif'—words taken out of context, meanings twisted, parts hidden. This isn't only physical alteration; it includes misinterpretation and selective emphasis. The Quran came to restore the pure monotheism of all prophets."
        },
        '5:116': {
            question: "Did the Quran get the Trinity wrong?",
            answer: "The Quran addresses what people actually worshipped, not official doctrine. Historical records show some Arabian Christians did elevate Mary to divine status. The Quran corrects worship of anything besides God alone."
        },
        '4:157': {
            question: "Why does Islam deny the crucifixion?",
            answer: "The Quran says Jesus was not killed but 'raised to God.' This doesn't deny the event appeared to happen—scholars differ on interpretation. What's clear: God protected His prophet, and Jesus will return before the Day of Judgment."
        },
        '10:94': {
            question: "Does the Quran tell Muslims to check with Christians?",
            answer: "This verse tells the Prophet to consult 'those who read the Book before you' about God's guidance through history—confirming prophetic continuity. It doesn't validate every teaching, but acknowledges shared heritage with People of the Book."
        },
        '2:62': {
            question: "Can Christians and Jews go to heaven?",
            answer: "'Those who believe, and Jews, Christians, Sabians—whoever believes in God and the Last Day and does good—will have their reward.' Faith and good deeds matter. God judges hearts, and His mercy is vast."
        },
        '3:64': {
            question: "What common ground do Muslims and Christians share?",
            answer: "'Come to a common word: worship God alone, associate nothing with Him.' This verse invites People of the Book to unite on pure monotheism—the original message of all prophets, including Jesus, Moses, and Muhammad."
        },
        '112:1': {
            question: "What is the core message of Islam?",
            answer: "'Say: He is God, the One. God, the Eternal. He begets not, nor was He begotten. And there is none comparable to Him.' Pure monotheism—the same message of Abraham, Moses, and Jesus before later theological developments."
        }
    };

    // ============================================
    // DOM ELEMENTS
    // ============================================

    let searchInput, searchBtn, suggestionsDropdown, suggestionsList;
    let verseSection, verseArabic, translationText, verseRef, verseName;
    let playAudioBtn, copyBtn, shareBtn;
    let chatInput, chatSendBtn, chatMessages;
    let analysisSection, analysisLoading, analysisContent, analysisGrid;
    let tafsirSection, tafsirContent, tafsirTabs;
    let exploreCards;

    // Current state
    let currentVerse = null;
    let currentAudio = null;

    // ============================================
    // INITIALIZATION
    // ============================================

    function init() {
        // Cache DOM elements
        searchInput = document.getElementById('qa-search-input');
        searchBtn = document.getElementById('qa-search-btn');
        suggestionsDropdown = document.getElementById('qa-search-suggestions');
        suggestionsList = document.getElementById('qa-suggestions-list');

        verseSection = document.getElementById('qa-verse-section');
        verseArabic = document.getElementById('qa-verse-arabic');
        translationText = document.getElementById('qa-translation-text');
        verseRef = document.getElementById('qa-verse-ref');
        verseName = document.getElementById('qa-verse-name');

        playAudioBtn = document.getElementById('qa-play-audio');
        copyBtn = document.getElementById('qa-copy-verse');
        shareBtn = document.getElementById('qa-share-verse');

        chatInput = document.getElementById('qa-chat-input');
        chatSendBtn = document.getElementById('qa-chat-send');
        chatMessages = document.getElementById('qa-chat-messages');

        analysisSection = document.getElementById('qa-analysis-section');
        analysisLoading = document.getElementById('qa-analysis-loading');
        analysisContent = document.getElementById('qa-analysis-content');
        analysisGrid = document.getElementById('qa-analysis-grid');

        tafsirSection = document.getElementById('qa-tafsir-section');
        tafsirContent = document.getElementById('qa-tafsir-content');
        tafsirTabs = document.querySelectorAll('.qa-tafsir-tab');

        // Support both old card format and new pill format
        exploreCards = document.querySelectorAll('.qa-explore-card, .qa-explore-pill');

        // Bind events
        bindEvents();

        // Check URL for verse reference
        checkUrlForVerse();

        console.log('[Quran Analyser] Initialized');
    }

    // ============================================
    // EVENT BINDINGS
    // ============================================

    function bindEvents() {
        // Search
        if (searchBtn) {
            searchBtn.addEventListener('click', handleSearch);
        }
        if (searchInput) {
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') handleSearch();
            });
            searchInput.addEventListener('input', debounce(handleSearchInput, 300));
        }

        // Explore cards/pills (commonly asked)
        exploreCards.forEach(card => {
            card.addEventListener('click', () => {
                const ref = card.dataset.ref;
                const question = card.dataset.question;
                const answer = card.dataset.answer;

                // Show answer section immediately if Q&A exists
                const answerSection = document.getElementById('qa-answer-section');
                const answerQuestion = document.getElementById('qa-answer-question');
                const answerText = document.getElementById('qa-answer-text');

                if (question && answer && answerSection && answerQuestion && answerText) {
                    window.currentVerseQA = { question, answer };
                    answerQuestion.textContent = question;
                    answerText.innerHTML = answer; // Use innerHTML to render links
                    bindAnswerLinks(); // Bind click handlers to verse links
                    answerSection.style.display = 'block';
                } else {
                    window.currentVerseQA = null;
                    if (answerSection) answerSection.style.display = 'none';
                }

                if (ref) {
                    loadVerseByRef(ref);
                }
            });
        });

        // Audio, Copy, Share
        if (playAudioBtn) playAudioBtn.addEventListener('click', handlePlayAudio);
        if (copyBtn) copyBtn.addEventListener('click', handleCopy);
        if (shareBtn) shareBtn.addEventListener('click', handleShare);

        // Verse Navigation
        const prevBtn = document.getElementById('qa-prev-verse');
        const nextBtn = document.getElementById('qa-next-verse');
        if (prevBtn) prevBtn.addEventListener('click', handlePrevVerse);
        if (nextBtn) nextBtn.addEventListener('click', handleNextVerse);

        // Chat
        if (chatSendBtn) chatSendBtn.addEventListener('click', handleChatSend);
        if (chatInput) {
            chatInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') handleChatSend();
            });
        }

        // Chat suggestion buttons (if any remain)
        document.querySelectorAll('.qa-chat-suggestion').forEach(btn => {
            btn.addEventListener('click', () => {
                const question = btn.dataset.question;
                if (question && chatInput) {
                    chatInput.value = question;
                    handleChatSend();
                }
            });
        });

        // Tab switching
        document.querySelectorAll('.qa-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const tabName = tab.dataset.tab;
                if (tabName) {
                    // Update tab buttons
                    document.querySelectorAll('.qa-tab').forEach(t => t.classList.remove('qa-tab--active'));
                    tab.classList.add('qa-tab--active');

                    // Update tab content
                    document.querySelectorAll('.qa-tab-content').forEach(c => c.classList.remove('qa-tab-content--active'));
                    const content = document.querySelector(`[data-content="${tabName}"]`);
                    if (content) content.classList.add('qa-tab-content--active');
                }
            });
        });

        // EXPLORE TAB SWITCHING
        document.querySelectorAll('.qa-explore-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const tabName = tab.dataset.tab;
                if (tabName) {
                    // Update tab buttons
                    document.querySelectorAll('.qa-explore-tab').forEach(t => t.classList.remove('qa-explore-tab--active'));
                    tab.classList.add('qa-explore-tab--active');

                    // Update panels
                    document.querySelectorAll('.qa-explore-panel').forEach(p => p.classList.remove('qa-explore-panel--active'));
                    const panel = document.getElementById(`qa-panel-${tabName}`);
                    if (panel) panel.classList.add('qa-explore-panel--active');
                }
            });
        });

        // SCROLL HINT - Hide arrow when user scrolls
        document.querySelectorAll('.qa-explore-scroll').forEach(scroll => {
            scroll.addEventListener('scroll', () => {
                const wrapper = scroll.parentElement;
                if (wrapper && wrapper.classList.contains('qa-scroll-wrapper')) {
                    if (scroll.scrollLeft > 30) {
                        wrapper.classList.add('scrolled');
                    } else {
                        wrapper.classList.remove('scrolled');
                    }
                }
            });
        });

        // VERSE PREVIEW MODAL for verse links in answers
        const previewModal = document.getElementById('qa-verse-preview-modal');
        const previewBackdrop = document.getElementById('qa-verse-preview-backdrop');
        const previewClose = document.getElementById('qa-verse-preview-close');
        const previewGoto = document.getElementById('qa-verse-preview-goto');

        // Close modal handlers
        if (previewBackdrop) {
            previewBackdrop.addEventListener('click', closeVersePreview);
        }
        if (previewClose) {
            previewClose.addEventListener('click', closeVersePreview);
        }
        if (previewGoto) {
            previewGoto.addEventListener('click', () => {
                if (window.previewedVerse) {
                    const verseToLoad = window.previewedVerse;
                    closeVersePreview();
                    loadVerseByRef(verseToLoad);
                }
            });
        }

        // Initialize tafsir accordion
        initTafsirAccordion();

        // Close suggestions when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.qa-search-section')) {
                hideSuggestions();
            }
        });
    }

    // ============================================
    // VERSE PREVIEW MODAL
    // ============================================

    function closeVersePreview() {
        const modal = document.getElementById('qa-verse-preview-modal');
        if (modal) modal.style.display = 'none';
        window.previewedVerse = null;
    }

    async function showVersePreview(verseRef) {
        const modal = document.getElementById('qa-verse-preview-modal');
        const refEl = document.getElementById('qa-verse-preview-ref');
        const arabicEl = document.getElementById('qa-verse-preview-arabic');
        const translationEl = document.getElementById('qa-verse-preview-translation');

        if (!modal || !refEl || !arabicEl || !translationEl) return;

        // Parse verse reference
        const parsed = parseVerseReference(verseRef);
        if (!parsed) return;

        // Store for Go to Verse button
        window.previewedVerse = verseRef;

        // Show modal with loading state
        refEl.textContent = `Verse ${parsed.surah}:${parsed.ayah}`;
        arabicEl.textContent = 'Loading...';
        translationEl.textContent = '';
        modal.style.display = 'flex';

        try {
            // Fetch verse data
            const [arabicRes, translationRes, surahRes] = await Promise.all([
                fetch(`https://api.alquran.cloud/v1/ayah/${parsed.surah}:${parsed.ayah}/ar.alafasy`),
                fetch(`https://api.alquran.cloud/v1/ayah/${parsed.surah}:${parsed.ayah}/en.sahih`),
                fetch(`https://api.alquran.cloud/v1/surah/${parsed.surah}`)
            ]);

            const [arabicData, translationData, surahData] = await Promise.all([
                arabicRes.json(),
                translationRes.json(),
                surahRes.json()
            ]);

            if (arabicData.data && translationData.data) {
                const surahName = surahData.data?.englishName || `Surah ${parsed.surah}`;
                refEl.textContent = `${surahName} ${parsed.surah}:${parsed.ayah}`;
                arabicEl.textContent = arabicData.data.text;
                translationEl.textContent = translationData.data.text;
            }
        } catch (error) {
            arabicEl.textContent = 'Could not load verse';
            translationEl.textContent = 'Please try again';
        }
    }

    function bindAnswerLinks() {
        // Bind click handlers to verse links inside the answer text
        document.querySelectorAll('.qa-verse-link').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const verse = link.dataset.verse;
                if (verse) {
                    // Show verse preview modal instead of navigating
                    showVersePreview(verse);
                }
            });
        });
    }

    // ============================================
    // SMART SEARCH
    // ============================================

    function handleSearchInput() {
        const query = searchInput.value.trim().toLowerCase();
        if (query.length < 2) {
            hideSuggestions();
            return;
        }

        // Try to parse as verse reference first
        const parsed = parseVerseReference(query);
        if (parsed) {
            // Valid reference, no need for suggestions
            hideSuggestions();
            return;
        }

        // Check famous verses
        const famousMatch = Object.keys(FAMOUS_VERSES).find(key => key.includes(query));
        if (famousMatch) {
            hideSuggestions();
            return;
        }

        // For longer queries that don't match, we might show AI suggestions later
        // For now, just hide
        hideSuggestions();
    }

    async function handleSearch() {
        const query = searchInput.value.trim();
        if (!query) {
            showNotification('Please enter a verse reference or search term', 'error');
            return;
        }

        // Try to parse as verse reference
        const parsed = parseVerseReference(query);
        if (parsed) {
            loadVerse(parsed.surah, parsed.ayah);
            return;
        }

        // Check famous verses
        const lowerQuery = query.toLowerCase();
        const famousMatch = Object.keys(FAMOUS_VERSES).find(key =>
            key.includes(lowerQuery) || lowerQuery.includes(key)
        );
        if (famousMatch) {
            const verse = FAMOUS_VERSES[famousMatch];
            loadVerse(verse.surah, verse.ayah);
            return;
        }

        // Check surah names
        const surahMatch = SURAHS.find(s =>
            s.en.toLowerCase().includes(lowerQuery) ||
            s.alt.some(a => a.includes(lowerQuery))
        );
        if (surahMatch) {
            loadVerse(surahMatch.num, 1);
            return;
        }

        // If nothing matched locally, try AI disambiguation
        await searchWithAI(query);
    }

    function parseVerseReference(input) {
        const query = input.toLowerCase().trim();

        // Patterns like "2:255", "2 255", "surah 2 verse 255", "baqarah 255"
        // Also supports ranges like "3:45-51" (uses the first verse)

        // Pattern: number:number or number:number-number (range)
        const colonPattern = /^(\d+)[:\s]+(\d+)(?:-\d+)?$/;
        let match = query.match(colonPattern);
        if (match) {
            return { surah: parseInt(match[1]), ayah: parseInt(match[2]) };
        }

        // Pattern: surah name + number
        const namePattern = /^([a-z\-]+)\s+(\d+)$/;
        match = query.match(namePattern);
        if (match) {
            const name = match[1];
            const ayah = parseInt(match[2]);

            // Find surah by name
            const surah = SURAHS.find(s =>
                s.en.toLowerCase().replace(/[^a-z]/g, '').includes(name.replace(/[^a-z]/g, '')) ||
                s.alt.some(a => a.includes(name))
            );

            if (surah) {
                return { surah: surah.num, ayah };
            }
        }

        // Pattern: number + surah name + number (e.g., "2 baqarah 255")
        const numNameNum = /^(\d+)\s+([a-z\-]+)\s+(\d+)$/;
        match = query.match(numNameNum);
        if (match) {
            return { surah: parseInt(match[1]), ayah: parseInt(match[3]) };
        }

        return null;
    }

    async function searchWithAI(query) {
        // Show loading state
        searchBtn.disabled = true;
        searchBtn.textContent = 'Searching...';

        try {
            const response = await fetch(`${BACKEND_API}/quran/search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query })
            });

            if (!response.ok) {
                throw new Error('Search failed');
            }

            const data = await response.json();

            if (data.success && data.suggestions && data.suggestions.length > 0) {
                showSuggestions(data.suggestions);
            } else if (data.success && data.verse) {
                // Direct match
                loadVerse(data.verse.surah, data.verse.ayah);
            } else {
                showNotification('No verses found. Try a different search term.', 'error');
            }

        } catch (error) {
            console.error('[Search Error]', error);
            // Fallback: just search in AlQuran API
            try {
                const fallbackRes = await fetch(`${API_BASE}/search/${encodeURIComponent(query)}/all/en.sahih`);
                const fallbackData = await fallbackRes.json();

                if (fallbackData.status === 'OK' && fallbackData.data.count > 0) {
                    const matches = fallbackData.data.matches.slice(0, 5);
                    const suggestions = matches.map(m => ({
                        surah: m.surah.number,
                        ayah: m.numberInSurah,
                        text: m.text.substring(0, 100) + '...',
                        surahName: m.surah.englishName
                    }));
                    showSuggestions(suggestions);
                } else {
                    showNotification('No verses found matching your search.', 'error');
                }
            } catch (fallbackError) {
                showNotification('Search unavailable. Try entering a verse reference like "2:255"', 'error');
            }
        } finally {
            searchBtn.disabled = false;
            searchBtn.textContent = 'Search';
        }
    }

    function showSuggestions(suggestions) {
        suggestionsList.innerHTML = suggestions.map(s => `
            <button class="qa-suggestion-item" data-surah="${s.surah}" data-ayah="${s.ayah}">
                <span class="qa-suggestion-ref">${s.surahName || 'Surah ' + s.surah} (${s.surah}:${s.ayah})</span>
                <span class="qa-suggestion-text">${s.text}</span>
            </button>
        `).join('');

        // Bind click events
        suggestionsList.querySelectorAll('.qa-suggestion-item').forEach(item => {
            item.addEventListener('click', () => {
                const surah = parseInt(item.dataset.surah);
                const ayah = parseInt(item.dataset.ayah);
                loadVerse(surah, ayah);
                hideSuggestions();
            });
        });

        suggestionsDropdown.style.display = 'block';
    }

    function hideSuggestions() {
        if (suggestionsDropdown) {
            suggestionsDropdown.style.display = 'none';
        }
    }

    // ============================================
    // VERSE LOADING
    // ============================================

    function loadVerseByRef(ref) {
        const [surah, ayah] = ref.split(':').map(Number);
        if (surah && ayah) {
            loadVerse(surah, ayah);
        }
    }

    async function loadVerse(surah, ayah) {
        // Show verse section
        if (verseSection) verseSection.style.display = 'block';

        // Scroll to verse with offset (so top is visible)
        const rect = verseSection.getBoundingClientRect();
        const scrollTop = window.pageYOffset + rect.top - 80;
        window.scrollTo({ top: scrollTop, behavior: 'smooth' });

        // Show loading in analysis
        if (analysisLoading) analysisLoading.style.display = 'flex';
        if (analysisContent) analysisContent.style.display = 'none';

        try {
            // Fetch Arabic and English simultaneously
            const [arabicRes, englishRes] = await Promise.all([
                fetch(`${API_BASE}/ayah/${surah}:${ayah}/quran-uthmani`),
                fetch(`${API_BASE}/ayah/${surah}:${ayah}/en.sahih`)
            ]);

            if (!arabicRes.ok || !englishRes.ok) {
                throw new Error('Failed to load verse');
            }

            const arabicData = await arabicRes.json();
            const englishData = await englishRes.json();

            if (arabicData.status !== 'OK') {
                throw new Error('Verse not found');
            }

            // Store current verse
            currentVerse = {
                surah,
                ayah,
                arabic: arabicData.data.text,
                english: englishData.data.text,
                surahName: arabicData.data.surah.englishName,
                surahArabicName: arabicData.data.surah.name
            };

            // Update display
            updateVerseDisplay(currentVerse);

            // Update URL
            updateUrl(surah, ayah);

            // Reset chat
            resetChat();

            // Auto-generate analysis
            generateAnalysis();

            // Load tafsir
            loadAllTafsirs(surah, ayah);

            // Check if this verse has a Q&A attached
            const verseQA = VERSE_QA[`${surah}:${ayah}`];
            const answerSection = document.getElementById('qa-answer-section');
            const answerQuestion = document.getElementById('qa-answer-question');
            const answerText = document.getElementById('qa-answer-text');

            if (verseQA && answerSection && answerQuestion && answerText) {
                window.currentVerseQA = verseQA;
                answerQuestion.textContent = verseQA.question;
                answerText.innerHTML = verseQA.answer;
                bindAnswerLinks();
                answerSection.style.display = 'block';
            } else {
                window.currentVerseQA = null;
                if (answerSection) answerSection.style.display = 'none';
            }

        } catch (error) {
            console.error('[Verse Load Error]', error);
            showNotification('Failed to load verse. Please try again.', 'error');
        }
    }

    function updateVerseDisplay(verse) {
        if (verseArabic) verseArabic.textContent = verse.arabic;
        if (translationText) translationText.textContent = verse.english;
        if (verseRef) verseRef.textContent = `Surah ${verse.surahName} (${verse.surah}:${verse.ayah})`;
        if (verseName) verseName.textContent = verse.surahArabicName;
    }

    function updateUrl(surah, ayah) {
        const url = new URL(window.location);
        url.searchParams.set('v', `${surah}:${ayah}`);
        window.history.replaceState({}, '', url);
    }

    function checkUrlForVerse() {
        const url = new URL(window.location);
        const verseParam = url.searchParams.get('v');
        if (verseParam) {
            const [surah, ayah] = verseParam.split(':').map(Number);
            if (surah && ayah) {
                loadVerse(surah, ayah);
            }
        }
    }

    // ============================================
    // AI ANALYSIS (Auto-generated)
    // ============================================

    async function generateAnalysis() {
        if (!currentVerse) return;

        if (analysisLoading) analysisLoading.style.display = 'flex';
        if (analysisContent) analysisContent.style.display = 'none';

        try {
            const response = await fetch(`${BACKEND_API}/quran/analyse`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    surah: currentVerse.surah,
                    ayah: currentVerse.ayah,
                    arabicText: currentVerse.arabic,
                    englishText: currentVerse.english
                })
            });

            if (!response.ok) {
                throw new Error('Analysis failed');
            }

            const data = await response.json();

            if (data.success && data.analysis) {
                displayAnalysis(data.analysis);
            } else {
                throw new Error(data.error || 'Unknown error');
            }

        } catch (error) {
            console.error('[Analysis Error]', error);
            // Show fallback content
            displayFallbackAnalysis();
        } finally {
            if (analysisLoading) analysisLoading.style.display = 'none';
            if (analysisContent) analysisContent.style.display = 'block';
        }
    }

    function displayAnalysis(analysis) {
        // NOTE: Answer section is now handled in loadVerse() - do not modify it here

        // Populate "What It Means" tab
        const themeText = document.getElementById('qa-theme-text');
        const wisdomText = document.getElementById('qa-wisdom-text');
        const reflectionList = document.getElementById('qa-reflection-list');

        if (themeText) {
            themeText.textContent = analysis.theme || 'This verse teaches a profound message about faith and guidance.';
        }

        if (wisdomText) {
            wisdomText.textContent = analysis.practicalWisdom || 'Reflect on how this guidance applies to your daily life.';
        }

        if (reflectionList && analysis.reflectionPrompts && analysis.reflectionPrompts.length) {
            reflectionList.innerHTML = analysis.reflectionPrompts.map(p => `<li>${p}</li>`).join('');
        } else if (reflectionList) {
            reflectionList.innerHTML = '<li>What does this verse mean to you?</li><li>How might this apply to modern life?</li>';
        }

        // Populate "Context" tab
        const contextText = document.getElementById('qa-context-text');
        if (contextText) {
            contextText.textContent = analysis.context || 'This verse was revealed during a specific time and situation in early Islamic history.';
        }

        // Populate "Language" tab
        const languageList = document.getElementById('qa-language-list');
        if (languageList && analysis.linguisticInsights && analysis.linguisticInsights.length) {
            languageList.innerHTML = analysis.linguisticInsights.map(i => `<li>${i}</li>`).join('');
        } else if (languageList) {
            languageList.innerHTML = '<li>Arabic contains nuances that translations may not fully capture.</li>';
        }

        // Reset to first tab
        document.querySelectorAll('.qa-tab').forEach((t, i) => {
            if (i === 0) t.classList.add('qa-tab--active');
            else t.classList.remove('qa-tab--active');
        });
        document.querySelectorAll('.qa-tab-content').forEach((c, i) => {
            if (i === 0) c.classList.add('qa-tab-content--active');
            else c.classList.remove('qa-tab-content--active');
        });
    }

    function displayFallbackAnalysis() {
        // NOTE: Answer section is now handled in loadVerse() - do not modify it here

        // Set fallback content
        const themeText = document.getElementById('qa-theme-text');
        const wisdomText = document.getElementById('qa-wisdom-text');
        const reflectionList = document.getElementById('qa-reflection-list');
        const contextText = document.getElementById('qa-context-text');
        const languageList = document.getElementById('qa-language-list');

        if (themeText) themeText.textContent = 'AI analysis is currently unavailable. Explore the tafsir section below for classical commentary.';
        if (wisdomText) wisdomText.textContent = 'Ask a question to get personalized insights.';
        if (reflectionList) reflectionList.innerHTML = '<li>What draws you to this verse?</li>';
        if (contextText) contextText.textContent = 'Historical context helps understand the circumstances of revelation.';
        if (languageList) languageList.innerHTML = '<li>Arabic word meanings add depth to understanding.</li>';
    }

    // ============================================
    // CHAT
    // ============================================

    function resetChat() {
        if (chatMessages) {
            chatMessages.innerHTML = `
                <div class="qa-chat-welcome">
                    <p>Have a question about this verse? Ask anything - I'll explain based on classical scholarship and context.</p>
                    <div class="qa-chat-suggestions">
                        <button class="qa-chat-suggestion" data-question="What is the context of this verse?">What's the context?</button>
                        <button class="qa-chat-suggestion" data-question="Why was this verse revealed?">Why was it revealed?</button>
                        <button class="qa-chat-suggestion" data-question="How do scholars interpret this verse?">How do scholars interpret it?</button>
                    </div>
                </div>
            `;

            // Rebind suggestion buttons
            chatMessages.querySelectorAll('.qa-chat-suggestion').forEach(btn => {
                btn.addEventListener('click', () => {
                    chatInput.value = btn.dataset.question;
                    handleChatSend();
                });
            });
        }
    }

    async function handleChatSend() {
        const question = chatInput.value.trim();
        if (!question || !currentVerse) return;

        // Clear input
        chatInput.value = '';

        // Hide welcome, add user message
        const welcome = chatMessages.querySelector('.qa-chat-welcome');
        if (welcome) welcome.remove();

        addChatMessage(question, 'user');

        // Show typing indicator
        const typingId = addTypingIndicator();

        try {
            const response = await fetch(`${BACKEND_API}/quran/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    surah: currentVerse.surah,
                    ayah: currentVerse.ayah,
                    arabicText: currentVerse.arabic,
                    englishText: currentVerse.english
                })
            });

            removeTypingIndicator(typingId);

            if (!response.ok) {
                throw new Error('Chat failed');
            }

            const data = await response.json();

            if (data.success && data.answer) {
                addChatMessageWithTypewriter(data.answer, 'ai');
            } else {
                throw new Error(data.error || 'Unknown error');
            }

        } catch (error) {
            removeTypingIndicator(typingId);
            console.error('[Chat Error]', error);
            addChatMessage('I apologize, but I\'m having trouble answering right now. Please try again.', 'ai');
        }
    }

    // Simple markdown parser for chat messages
    function parseMarkdown(text) {
        return text
            // Bold: **text** or __text__
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/__(.+?)__/g, '<strong>$1</strong>')
            // Italic: *text* or _text_
            .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
            .replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>')
            // Line breaks
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br>');
    }

    function addChatMessage(text, sender) {
        const messageDiv = document.createElement('div');
        messageDiv.className = `qa-chat-message qa-chat-message--${sender}`;
        const formattedText = sender === 'ai' ? `<p>${parseMarkdown(text)}</p>` : text;
        messageDiv.innerHTML = `<div class="qa-chat-bubble">${formattedText}</div>`;
        chatMessages.appendChild(messageDiv);
        chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    // Typewriter effect for AI responses
    async function addChatMessageWithTypewriter(text, sender) {
        const messageDiv = document.createElement('div');
        messageDiv.className = `qa-chat-message qa-chat-message--${sender}`;
        const bubble = document.createElement('div');
        bubble.className = 'qa-chat-bubble';
        messageDiv.appendChild(bubble);
        chatMessages.appendChild(messageDiv);

        // Split into words
        const words = text.split(' ');
        let currentText = '';

        for (let i = 0; i < words.length; i++) {
            currentText += (i === 0 ? '' : ' ') + words[i];
            bubble.innerHTML = `<p>${parseMarkdown(currentText)}</p>`;

            // Only auto-scroll if user is near the bottom (within 100px)
            const isNearBottom = chatMessages.scrollHeight - chatMessages.scrollTop - chatMessages.clientHeight < 100;
            if (isNearBottom) {
                chatMessages.scrollTop = chatMessages.scrollHeight;
            }

            // Variable speed - faster for shorter words, slight pause after punctuation
            const word = words[i];
            let delay = 30; // Base delay
            if (word.endsWith('.') || word.endsWith('?') || word.endsWith('!')) {
                delay = 80; // Longer pause after sentences
            } else if (word.endsWith(',') || word.endsWith(':')) {
                delay = 50; // Medium pause after clauses
            }

            await new Promise(r => setTimeout(r, delay));
        }
    }

    function addTypingIndicator() {
        const id = 'typing-' + Date.now();
        const div = document.createElement('div');
        div.id = id;
        div.className = 'qa-chat-message qa-chat-message--ai';
        div.innerHTML = `<div class="qa-chat-bubble">Thinking...</div>`;
        chatMessages.appendChild(div);
        chatMessages.scrollTop = chatMessages.scrollHeight;
        return id;
    }

    function removeTypingIndicator(id) {
        const el = document.getElementById(id);
        if (el) el.remove();
    }

    // ============================================
    // TAFSIR - EXPANDABLE ACCORDION
    // ============================================

    // Tafsir IDs from api.quran-tafseer.com
    const TAFSIR_SOURCES = {
        'ibn-kathir': { id: 1, name: 'Ibn Kathir' },
        'jalalayn': { id: 2, name: 'Al-Jalalayn' },
        'tabari': { id: 3, name: 'Al-Tabari' }
    };

    let loadedTafsirs = {};

    function initTafsirAccordion() {
        const accordion = document.getElementById('qa-tafsir-accordion');
        if (!accordion) return;

        accordion.querySelectorAll('.qa-tafsir-toggle').forEach(toggle => {
            toggle.addEventListener('click', () => {
                const item = toggle.closest('.qa-tafsir-item');
                const tafsirKey = item.dataset.tafsir;
                const body = item.querySelector('.qa-tafsir-body');
                const isOpen = toggle.classList.contains('qa-tafsir-toggle--active');

                if (isOpen) {
                    // Close
                    toggle.classList.remove('qa-tafsir-toggle--active');
                    body.classList.remove('qa-tafsir-body--open');
                } else {
                    // Open
                    toggle.classList.add('qa-tafsir-toggle--active');
                    body.classList.add('qa-tafsir-body--open');

                    // Load if not already loaded
                    if (currentVerse && !loadedTafsirs[tafsirKey]) {
                        loadSingleTafsir(tafsirKey, currentVerse.surah, currentVerse.ayah);
                    }
                }
            });
        });
    }

    async function loadAllTafsirs(surah, ayah) {
        if (!tafsirSection) return;

        tafsirSection.style.display = 'block';
        loadedTafsirs = {}; // Reset

        // Load Ibn Kathir by default (first open)
        await loadSingleTafsir('ibn-kathir', surah, ayah);
    }

    async function loadSingleTafsir(tafsirKey, surah, ayah) {
        const bodyEl = document.getElementById(`tafsir-${tafsirKey}`);
        if (!bodyEl) return;

        const source = TAFSIR_SOURCES[tafsirKey];
        if (!source) return;

        bodyEl.innerHTML = '<p class="qa-tafsir-loading">Loading commentary...</p>';

        try {
            // Use api.quran-tafseer.com API
            const response = await fetch(`https://api.quran-tafseer.com/tafseer/${source.id}/${surah}/${ayah}`);

            if (!response.ok) {
                throw new Error('API error');
            }

            const data = await response.json();

            if (data && data.text) {
                // Format the text with proper paragraphs
                const formattedText = formatTafsirText(data.text);
                bodyEl.innerHTML = formattedText;
                loadedTafsirs[tafsirKey] = true;
            } else {
                bodyEl.innerHTML = '<p>Commentary not available for this verse.</p>';
            }

        } catch (error) {
            console.error(`[Tafsir ${tafsirKey} Error]`, error);

            // Fallback: try quran.com API for Ibn Kathir
            if (tafsirKey === 'ibn-kathir') {
                try {
                    const fallbackRes = await fetch(`https://api.quran.com/api/v4/tafsirs/169/by_ayah/${surah}:${ayah}`);
                    if (fallbackRes.ok) {
                        const fallbackData = await fallbackRes.json();
                        if (fallbackData.tafsir?.text) {
                            const cleanText = fallbackData.tafsir.text.replace(/<[^>]*>/g, '');
                            bodyEl.innerHTML = formatTafsirText(cleanText);
                            loadedTafsirs[tafsirKey] = true;
                            return;
                        }
                    }
                } catch (e) {
                    console.error('[Fallback Tafsir Error]', e);
                }
            }

            bodyEl.innerHTML = '<p>Unable to load this commentary. Please try again later.</p>';
        }
    }

    function formatTafsirText(text) {
        if (!text) return '<p>No commentary available.</p>';

        // Split into paragraphs on double newlines or periods followed by capital letters
        let paragraphs = text.split(/\n\n+|\. (?=[A-Z])/);

        // If not enough paragraphs, try splitting on long stretches
        if (paragraphs.length < 3 && text.length > 500) {
            // Split every ~300 characters at sentence boundaries
            const sentences = text.split(/\. /);
            paragraphs = [];
            let current = '';

            sentences.forEach(sentence => {
                if (current.length + sentence.length > 400 && current.length > 100) {
                    paragraphs.push(current.trim());
                    current = sentence;
                } else {
                    current += (current ? '. ' : '') + sentence;
                }
            });
            if (current) paragraphs.push(current.trim());
        }

        // Ensure each paragraph ends with proper punctuation
        return paragraphs
            .filter(p => p.trim().length > 0)
            .map(p => {
                let trimmed = p.trim();
                if (!trimmed.endsWith('.') && !trimmed.endsWith('؟') && !trimmed.endsWith('!')) {
                    trimmed += '.';
                }
                return `<p>${trimmed}</p>`;
            })
            .join('');
    }

    // ============================================
    // AUDIO, COPY, SHARE
    // ============================================

    async function handlePlayAudio() {
        if (!currentVerse) return;

        if (currentAudio) {
            currentAudio.pause();
            currentAudio = null;
            updatePlayButton(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/ayah/${currentVerse.surah}:${currentVerse.ayah}/ar.alafasy`);
            const data = await response.json();

            if (data.status === 'OK' && data.data.audio) {
                currentAudio = new Audio(data.data.audio);
                currentAudio.play();
                updatePlayButton(true);

                currentAudio.addEventListener('ended', () => {
                    currentAudio = null;
                    updatePlayButton(false);
                });
            }
        } catch (error) {
            console.error('[Audio Error]', error);
            showNotification('Audio unavailable', 'error');
        }
    }

    function updatePlayButton(isPlaying) {
        if (!playAudioBtn) return;

        if (isPlaying) {
            playAudioBtn.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="6" y="4" width="4" height="16"></rect>
                    <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
                Pause
            `;
        } else {
            playAudioBtn.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
                Listen
            `;
        }
    }

    function handleCopy() {
        if (!currentVerse) return;

        const text = `${currentVerse.arabic}\n\n${currentVerse.english}\n\n— Surah ${currentVerse.surahName} (${currentVerse.surah}:${currentVerse.ayah})`;

        navigator.clipboard.writeText(text)
            .then(() => showNotification('Copied to clipboard!', 'success'))
            .catch(() => showNotification('Failed to copy', 'error'));
    }

    function handleShare() {
        if (!currentVerse) return;

        const url = window.location.href;
        const title = `Quran ${currentVerse.surah}:${currentVerse.ayah}`;

        if (navigator.share) {
            navigator.share({ title, url }).catch(() => { });
        } else {
            navigator.clipboard.writeText(url)
                .then(() => showNotification('Link copied!', 'success'))
                .catch(() => showNotification('Failed to share', 'error'));
        }
    }

    // ============================================
    // VERSE NAVIGATION
    // ============================================

    async function handlePrevVerse() {
        if (!currentVerse) return;

        // Clear the Q&A since we're navigating away from a specific verse
        window.currentVerseQA = null;
        const answerSection = document.getElementById('qa-answer-section');
        if (answerSection) answerSection.style.display = 'none';

        let newSurah = currentVerse.surah;
        let newAyah = currentVerse.ayah - 1;

        // If at first verse of surah, go to last verse of previous surah
        if (newAyah < 1) {
            if (newSurah > 1) {
                newSurah -= 1;
                // Get the verse count of the previous surah
                try {
                    const surahInfo = await fetch(`${API_BASE}/surah/${newSurah}`);
                    const surahData = await surahInfo.json();
                    newAyah = surahData.data.numberOfAyahs;
                } catch (e) {
                    newAyah = 1; // Fallback
                }
            } else {
                showNotification('This is the first verse of the Quran', 'info');
                return;
            }
        }

        loadVerse(newSurah, newAyah);
    }

    async function handleNextVerse() {
        if (!currentVerse) return;

        // Clear the Q&A since we're navigating away from a specific verse
        window.currentVerseQA = null;
        const answerSection = document.getElementById('qa-answer-section');
        if (answerSection) answerSection.style.display = 'none';

        let newSurah = currentVerse.surah;
        let newAyah = currentVerse.ayah + 1;

        // Check if we're past the last verse of current surah
        try {
            const surahInfo = await fetch(`${API_BASE}/surah/${newSurah}`);
            const surahData = await surahInfo.json();
            const totalVerses = surahData.data.numberOfAyahs;

            if (newAyah > totalVerses) {
                // Go to first verse of next surah
                if (newSurah < 114) {
                    newSurah += 1;
                    newAyah = 1;
                } else {
                    showNotification('This is the last verse of the Quran', 'info');
                    return;
                }
            }
        } catch (e) {
            // If API fails, just try loading the verse
        }

        loadVerse(newSurah, newAyah);
    }

    // ============================================
    // UTILITIES
    // ============================================

    function debounce(func, wait) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    function showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `qa-notification qa-notification--${type}`;
        notification.textContent = message;

        // Add styles if not present
        if (!document.getElementById('qa-notification-styles')) {
            const style = document.createElement('style');
            style.id = 'qa-notification-styles';
            style.textContent = `
                .qa-notification {
                    position: fixed;
                    bottom: 24px;
                    left: 50%;
                    transform: translateX(-50%) translateY(100px);
                    padding: 14px 24px;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 500;
                    z-index: 9999;
                    animation: qa-slide-up 0.3s ease forwards;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                }
                .qa-notification--success { background: #059669; color: white; }
                .qa-notification--error { background: #dc2626; color: white; }
                .qa-notification--info { background: #1a2c2c; color: #e6b54a; }
                @keyframes qa-slide-up {
                    to { transform: translateX(-50%) translateY(0); }
                }
            `;
            document.head.appendChild(style);
        }

        document.body.appendChild(notification);
        setTimeout(() => {
            notification.style.animation = 'qa-slide-up 0.3s ease reverse forwards';
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }

    // ============================================
    // STARTUP
    // ============================================

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
